import type {
  BundleAdministrationArea,
  BundleAdministrationTask,
  BundleAdministrationVersion,
  BundleReviewDecisionCommand,
  BundleReleasePreview,
  CatalogueMarket,
  Goal2AdministrationCapabilityProjection,
} from "@reminder-tracker/backend-contracts";
import { useMutation, useQuery } from "convex/react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import type { BundleVersionDefinition } from "@reminder-tracker/domain";
import { compileEverydayBundleCandidate } from "../../../../../config/scrum-427-bundle-candidate";

import {
  decideBundleReview,
  getBundleAdministrationVersion,
  getGoal2AdministrationCapabilities,
  listBundleAdministrationTasks,
  listBundleWithdrawalReviewTasks,
  previewBundleRelease,
  previewBundleWithdrawal,
  publishCompletionProfileCandidate,
  publishEverydayCompletionBindings,
  publishBundleRelease,
  reviewBundleWithdrawal,
  saveBundleDraft,
  saveBundleReleaseCandidate,
  submitBundleReview,
  withdrawBundleVersion,
} from "../../backend/functions";
import { formatAdministrationTime } from "../../ui/administration-time";
import { AdministrationWorkSurface } from "../../ui/administration-surface";
import { BundleRetirementTasks } from "./bundle-retirement";

const areaLabels = {
  author: "Draft bundles",
  review: "Review bundles",
  release: "Prepare release",
  retirement: "Retire from discovery",
  safety: "Safety withdrawal",
} as const;

const newBundleContent: BundleAdministrationVersion["content"] = {
  kind: "review-pack",
  title: "New reviewed bundle",
  summary: "Explain the bounded reminder set and when it may help.",
  searchAliases: [],
  nonCompletenessCopy:
    "This creates separate reminders. It does not complete the underlying tasks.",
  availabilityNotes: "Review each reminder and date before saving.",
  evidenceReferences: [],
  anchorQuestion: null,
  members: [],
};

export function candidateContent(
  candidate: BundleVersionDefinition,
): BundleAdministrationVersion["content"] {
  return {
    kind: candidate.kind,
    title: candidate.title,
    summary: candidate.summary,
    searchAliases: [...candidate.searchAliases],
    nonCompletenessCopy: candidate.nonCompletenessCopy,
    availabilityNotes: candidate.availabilityNotes,
    evidenceReferences: candidate.evidenceReferences.map((reference) => ({ ...reference })),
    anchorQuestion: candidate.anchorQuestion,
    ...(candidate.sectionLayout === undefined
      ? {}
      : { sectionLayout: structuredClone(candidate.sectionLayout) }),
    members: candidate.members.map((member) => ({
      ...member,
      dateRelationship:
        member.dateRelationship.kind === "independent-confirmed"
          ? { kind: "independent-confirmed" as const }
          : { kind: "anchor-derived" as const, rule: { ...member.dateRelationship.rule } },
    })),
  };
}

function secureKey(prefix: "corr" | "cmd") {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
}

function grantedAreas(projection: Goal2AdministrationCapabilityProjection) {
  const granted = new Set(
    projection.capabilities.filter((entry) => entry.granted).map((entry) => entry.capability),
  );
  return [
    ...(granted.has("catalogue:draft-edit-preview") ? (["author"] as const) : []),
    ...(granted.has("catalogue:review") ? (["review"] as const) : []),
    ...(granted.has("catalogue:release") ? (["release", "retirement"] as const) : []),
    ...(granted.has("catalogue:safety-withdraw") ? (["safety"] as const) : []),
  ];
}

export function BundleTaskNavigation({
  projection,
  active,
  onSelect,
}: Readonly<{
  projection: Goal2AdministrationCapabilityProjection;
  active: BundleAdministrationArea;
  onSelect: (area: BundleAdministrationArea) => void;
}>): React.JSX.Element | null {
  const areas = grantedAreas(projection);
  if (areas.length === 0) return null;
  return (
    <div className="bundle-task-tabs" role="tablist" aria-label="Bundle administration tasks">
      {areas.map((area) => (
        <button
          key={area}
          type="button"
          role="tab"
          aria-selected={active === area}
          onClick={() => onSelect(area)}
        >
          {areaLabels[area]}
        </button>
      ))}
    </div>
  );
}

export function BundleAdministrationTasks(): React.JSX.Element {
  const [correlationId] = useState(() => secureKey("corr"));
  const projection = useQuery(getGoal2AdministrationCapabilities, { correlationId });
  const available = projection === undefined ? [] : grantedAreas(projection);
  const [requestedArea, setRequestedArea] = useState<BundleAdministrationArea>("author");
  const active = available.includes(requestedArea) ? requestedArea : (available[0] ?? "author");
  return (
    <AdministrationWorkSurface
      id="bundle-administration-heading"
      className="bundle-admin"
      title="Bundle work"
      purpose="Choose one editorial stage, then work on the bounded bundle versions assigned to that stage."
      state={{
        label:
          projection === undefined
            ? "Checking responsibilities…"
            : `${areaLabels[active]} · ${projection.environment}`,
        tone: projection === undefined ? "neutral" : available.length === 0 ? "healthy" : "monitor",
      }}
      consequence="Changes remain inside the reviewed reference-content workflow. This area cannot open or change customer reminders, recipients, drafts or completion history."
    >
      {projection === undefined ? (
        <p role="status">Checking bundle responsibilities…</p>
      ) : available.length === 0 ? (
        <p>No bundle task is assigned to this administrator.</p>
      ) : (
        <>
          <BundleTaskNavigation
            projection={projection}
            active={active}
            onSelect={setRequestedArea}
          />
          <div role="tabpanel" aria-label={areaLabels[active]}>
            {active === "author" ? <AuthorTasks /> : null}
            {active === "review" ? <ReviewTasks /> : null}
            {active === "release" ? <ReleaseTasks environment={projection.environment} /> : null}
            {active === "retirement" ? (
              <BundleRetirementTasks environment={projection.environment} />
            ) : null}
            {active === "safety" ? <SafetyTasks environment={projection.environment} /> : null}
          </div>
        </>
      )}
    </AdministrationWorkSurface>
  );
}

function useTaskPage(area: BundleAdministrationArea) {
  const [correlationId] = useState(() => secureKey("corr"));
  const [position, setPosition] = useState<{
    area: BundleAdministrationArea;
    cursors: Array<string | null>;
  }>({ area, cursors: [null] });
  const cursors = position.area === area ? position.cursors : [null];
  const page = useQuery(listBundleAdministrationTasks, {
    area,
    correlationId,
    cursor: cursors.at(-1) ?? null,
  });
  return page === undefined
    ? undefined
    : {
        ...page,
        pageNumber: cursors.length,
        previous: () => setPosition({ area, cursors: cursors.slice(0, -1) }),
        next: () => {
          if (page.cursor !== null) setPosition({ area, cursors: [...cursors, page.cursor] });
        },
      };
}

function TaskPagination({ page }: Readonly<{ page: NonNullable<ReturnType<typeof useTaskPage>> }>) {
  if (page.pageNumber === 1 && page.cursor === null) return null;
  return (
    <nav aria-label="Bundle task pages" className="bundle-task-pagination">
      <button type="button" disabled={page.pageNumber === 1} onClick={page.previous}>
        Previous page
      </button>
      <span>Page {page.pageNumber}</span>
      <button type="button" disabled={page.cursor === null} onClick={page.next}>
        Next page
      </button>
    </nav>
  );
}

function TaskList({
  area,
  selected,
  onSelect,
}: Readonly<{
  area: BundleAdministrationArea;
  selected: BundleAdministrationTask | null;
  onSelect: (task: BundleAdministrationTask) => void;
}>): React.JSX.Element {
  const page = useTaskPage(area);
  if (page === undefined)
    return <p role="status">Reading current {areaLabels[area].toLowerCase()}…</p>;
  if (page.items.length === 0)
    return (
      <>
        <p>No current {areaLabels[area].toLowerCase()} tasks on this page.</p>
        <TaskPagination page={page} />
      </>
    );
  return (
    <>
      <ul className="bundle-task-list">
        {page.items.map((task) => (
          <li key={`${task.bundleId}/${task.version}`}>
            <button
              type="button"
              aria-pressed={
                selected?.bundleId === task.bundleId && selected.version === task.version
              }
              onClick={() => onSelect(task)}
            >
              <strong>{task.title}</strong>
              <span>
                {task.market} · v{task.version} · revision {task.revision} · {task.memberCount}{" "}
                {task.memberCount === 1 ? "reminder" : "reminders"}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <TaskPagination page={page} />
    </>
  );
}

function useExactVersion(
  area: BundleAdministrationArea,
  selected: BundleAdministrationTask | null,
) {
  const [correlationId] = useState(() => secureKey("corr"));
  return useQuery(
    getBundleAdministrationVersion,
    selected === null
      ? "skip"
      : { area, bundleId: selected.bundleId, version: selected.version, correlationId },
  );
}

function VersionSummary({ version }: Readonly<{ version: BundleAdministrationVersion }>) {
  return (
    <article className="bundle-version-summary">
      <p className="eyebrow">
        {version.market} · {version.state} · version {version.version}, revision {version.revision}
      </p>
      <h4>{version.content.title}</h4>
      <p>{version.content.summary}</p>
      <dl>
        <div>
          <dt>Kind</dt>
          <dd>{version.content.kind === "life-event" ? "Life event" : "Review pack"}</dd>
        </div>
        <div>
          <dt>Members</dt>
          <dd>{version.content.members.length}</dd>
        </div>
        <div>
          <dt>Evidence links</dt>
          <dd>{version.content.evidenceReferences.length}</dd>
        </div>
      </dl>
      <ol>
        {version.content.members.map((member) => (
          <li key={member.memberId}>
            {member.presetId} v{member.presetVersion} · {member.startingSelection}
          </li>
        ))}
      </ol>
    </article>
  );
}

function AuthorTasks(): React.JSX.Element {
  const candidates = useMemo(() => compileEverydayBundleCandidate().bundles, []);
  const [correlationId] = useState(() => secureKey("corr"));
  const [selected, setSelected] = useState<BundleAdministrationTask | null>(null);
  const [creating, setCreating] = useState(false);
  const [bundleId, setBundleId] = useState("");
  const [market, setMarket] = useState<CatalogueMarket>("GB");
  const version = useExactVersion("author", selected);
  const save = useMutation(saveBundleDraft);
  const submit = useMutation(submitBundleReview);
  const [contentText, setContentText] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const saveCommand = useRef(secureKey("cmd"));
  const submitCommand = useRef(secureKey("cmd"));
  const displayed =
    contentText ??
    (version
      ? JSON.stringify(version.content, null, 2)
      : creating
        ? JSON.stringify(newBundleContent, null, 2)
        : "");
  const saveCurrent = async () => {
    if ((!version && !creating) || busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      const content = JSON.parse(displayed) as BundleAdministrationVersion["content"];
      const result = await save({
        bundleId: version?.bundleId ?? bundleId,
        market: version?.market ?? market,
        content,
        version: version?.version ?? null,
        expectedRevision: version?.revision ?? null,
        commandKey: saveCommand.current,
        correlationId,
      });
      setFeedback(`Draft saved at revision ${result.revision}. Reopen it before another edit.`);
      saveCommand.current = secureKey("cmd");
      setSelected(null);
      setCreating(false);
      setBundleId("");
      setContentText(null);
    } catch {
      setFeedback("The draft was not saved. Check the current revision and reviewed fields.");
    } finally {
      setBusy(false);
    }
  };
  const submitCurrent = async () => {
    if (!version || busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      await submit({
        bundleId: version.bundleId,
        version: version.version,
        expectedRevision: version.revision,
        contentDigest: version.contentDigest,
        commandKey: submitCommand.current,
        correlationId,
      });
      setFeedback("Submitted for exact-revision review. No bundle was published.");
      submitCommand.current = secureKey("cmd");
      setSelected(null);
      setContentText(null);
    } catch {
      setFeedback("Submission was refused. Reopen the latest draft and check its dependencies.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bundle-task-grid">
      <div>
        <label htmlFor="bundle-candidate-picker">Load an approved everyday bundle</label>
        <select
          id="bundle-candidate-picker"
          value=""
          onChange={(event) => {
            const candidate = candidates.find((entry) => entry.bundleId === event.target.value);
            if (!candidate) return;
            setCreating(true);
            setSelected(null);
            setBundleId(candidate.bundleId);
            setMarket(candidate.market);
            setContentText(JSON.stringify(candidateContent(candidate), null, 2));
            setFeedback(
              "Candidate loaded locally. Review it before creating an unpublished draft.",
            );
          }}
        >
          <option value="">Choose one of {candidates.length} reviewed candidates</option>
          {candidates.map((candidate) => (
            <option key={candidate.bundleId} value={candidate.bundleId}>
              {candidate.market} · {candidate.title}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-pressed={creating}
          onClick={() => {
            setCreating(true);
            setSelected(null);
            setContentText(JSON.stringify(newBundleContent, null, 2));
            setFeedback(null);
          }}
        >
          Start a new bundle draft
        </button>
        <TaskList
          area="author"
          selected={selected}
          onSelect={(task) => {
            setCreating(false);
            setSelected(task);
            setContentText(null);
            setFeedback(null);
          }}
        />
      </div>
      {selected && version === undefined ? <p role="status">Opening exact draft…</p> : null}
      {version || creating ? (
        <div>
          {version ? <VersionSummary version={version} /> : null}
          {creating ? (
            <fieldset>
              <legend>New bundle identity</legend>
              <label>
                Stable bundle reference
                <input value={bundleId} onChange={(event) => setBundleId(event.target.value)} />
              </label>
              <label>
                Market
                <select
                  value={market}
                  onChange={(event) => setMarket(event.target.value as CatalogueMarket)}
                >
                  {(["GB", "US", "AE", "SA", "IN", "SG", "AU"] as const).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
            </fieldset>
          ) : null}
          <label htmlFor="bundle-editor-fields">Reviewed bundle fields</label>
          <textarea
            id="bundle-editor-fields"
            rows={18}
            spellCheck={false}
            value={displayed}
            onChange={(event) => setContentText(event.target.value)}
          />
          <div className="push-policy-actions">
            <button
              type="button"
              disabled={busy || (creating && bundleId.trim() === "")}
              onClick={() => void saveCurrent()}
            >
              {creating ? "Create unpublished draft" : "Save exact draft revision"}
            </button>
            {version ? (
              <button
                type="button"
                disabled={busy || contentText !== null}
                onClick={() => void submitCurrent()}
              >
                Submit unchanged revision for review
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {feedback ? <p role="status">{feedback}</p> : null}
    </div>
  );
}

function ReviewTasks(): React.JSX.Element {
  const [correlationId] = useState(() => secureKey("corr"));
  const [selected, setSelected] = useState<BundleAdministrationTask | null>(null);
  const version = useExactVersion("review", selected);
  const decide = useMutation(decideBundleReview);
  const [decision, setDecision] = useState<"approve" | "return" | "reject">("approve");
  const [reason, setReason] = useState<BundleReviewDecisionCommand["reason"]>("approved");
  const [rationale, setRationale] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const command = useRef(secureKey("cmd"));
  const send = async () => {
    if (!version || !confirmed) return;
    setFeedback(null);
    try {
      await decide({
        bundleId: version.bundleId,
        version: version.version,
        expectedRevision: version.revision,
        contentDigest: version.contentDigest,
        decision,
        reason,
        rationale: decision === "approve" ? null : rationale,
        commandKey: command.current,
        correlationId,
      });
      command.current = secureKey("cmd");
      setFeedback(
        decision === "approve"
          ? "Exact revision approved. It remains unavailable until a release is published."
          : `Exact revision ${decision === "return" ? "returned" : "rejected"}.`,
      );
      setSelected(null);
      setConfirmed(false);
    } catch {
      setFeedback("The review was refused. Reopen the latest immutable revision.");
    }
  };
  return (
    <div className="bundle-task-grid">
      <TaskList
        area="review"
        selected={selected}
        onSelect={(task) => {
          setSelected(task);
          setDecision("approve");
          setReason("approved");
          setRationale("");
          setConfirmed(false);
          setFeedback(null);
        }}
      />
      {version ? (
        <div>
          <VersionSummary version={version} />
          <label>
            Decision
            <select
              value={decision}
              onChange={(event) => {
                const next = event.target.value as typeof decision;
                setDecision(next);
                setReason(next === "approve" ? "approved" : "editorial-correction");
              }}
            >
              <option value="approve">Approve exact revision</option>
              <option value="return">Return for correction</option>
              <option value="reject">Reject this version</option>
            </select>
          </label>
          {decision !== "approve" ? (
            <>
              <label>
                Closed reason
                <select
                  value={reason}
                  onChange={(event) =>
                    setReason(event.target.value as BundleReviewDecisionCommand["reason"])
                  }
                >
                  <option value="evidence-incomplete">Evidence incomplete</option>
                  <option value="dependency-unavailable">Dependency unavailable</option>
                  <option value="market-safety">Market or safety concern</option>
                  <option value="editorial-correction">Editorial correction</option>
                  <option value="scope-unsupported">Scope unsupported</option>
                </select>
              </label>
              <label>
                Review rationale
                <textarea
                  maxLength={500}
                  value={rationale}
                  onChange={(event) => setRationale(event.target.value)}
                />
              </label>
            </>
          ) : null}
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            I reviewed this exact revision and its evidence.
          </label>
          <button
            type="button"
            disabled={!confirmed || (decision !== "approve" && rationale.trim() === "")}
            onClick={() => void send()}
          >
            Record {decision}
          </button>
        </div>
      ) : null}
      {feedback ? <p role="status">{feedback}</p> : null}
      <WithdrawalReviewTasks />
    </div>
  );
}

function WithdrawalReviewTasks(): React.JSX.Element | null {
  const [correlationId] = useState(() => secureKey("corr"));
  const page = useQuery(listBundleWithdrawalReviewTasks, { correlationId });
  const review = useMutation(reviewBundleWithdrawal);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<"withdrawal-confirmed" | "replacement-required">(
    "withdrawal-confirmed",
  );
  const [rationale, setRationale] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const command = useRef(secureKey("cmd"));
  if (page === undefined) return <p role="status">Checking withdrawal reviews…</p>;
  if (page.items.length === 0 && feedback === null) return null;
  const selected = page.items.find((item) => item.withdrawalId === selectedId) ?? null;
  const send = async () => {
    if (!selected || !confirmed || rationale.trim() === "") return;
    setFeedback(null);
    try {
      await review({
        withdrawalId: selected.withdrawalId,
        expectedRevision: selected.revision,
        outcome,
        rationale,
        commandKey: command.current,
        correlationId,
      });
      command.current = secureKey("cmd");
      setSelectedId(null);
      setConfirmed(false);
      setRationale("");
      setFeedback("Retrospective decision recorded. The bundle version remains withdrawn.");
    } catch {
      setFeedback("The withdrawal review was refused. Reopen the current review task.");
    }
  };
  return (
    <section className="bundle-withdrawal-reviews" aria-labelledby="withdrawal-review-heading">
      <h4 id="withdrawal-review-heading">Withdrawal reviews</h4>
      <p>Independent review never restores withdrawn content.</p>
      <ul className="bundle-task-list">
        {page.items.map((item) => (
          <li key={item.withdrawalId}>
            <button
              type="button"
              aria-pressed={selectedId === item.withdrawalId}
              onClick={() => {
                setSelectedId(item.withdrawalId);
                setOutcome("withdrawal-confirmed");
                setRationale("");
                setConfirmed(false);
              }}
            >
              <strong>
                {item.bundleId} v{item.bundleVersion}
              </strong>
              <span>
                {item.reason} · due{" "}
                {formatAdministrationTime(item.retrospectiveReviewDueAtEpochMilliseconds)}
                {item.governanceAlert ? " · overdue" : ""}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {selected ? (
        <div>
          <label>
            Retrospective outcome
            <select
              value={outcome}
              onChange={(event) => setOutcome(event.target.value as typeof outcome)}
            >
              <option value="withdrawal-confirmed">Keep withdrawn</option>
              <option value="replacement-required">Replacement required</option>
            </select>
          </label>
          <label>
            Review rationale
            <textarea
              maxLength={500}
              value={rationale}
              onChange={(event) => setRationale(event.target.value)}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            Record this review without reinstating the version.
          </label>
          <button
            type="button"
            disabled={!confirmed || rationale.trim() === ""}
            onClick={() => void send()}
          >
            Record withdrawal review
          </button>
        </div>
      ) : null}
      {page.truncated ? <p className="notice">Only the first 100 reviews are shown.</p> : null}
      {feedback ? <p role="status">{feedback}</p> : null}
    </section>
  );
}

function ReleaseTasks({ environment }: Readonly<{ environment: string }>): React.JSX.Element {
  const [correlationId] = useState(() => secureKey("corr"));
  const page = useTaskPage("release");
  const saveCandidate = useMutation(saveBundleReleaseCandidate);
  const publish = useMutation(publishBundleRelease);
  const [selected, setSelected] = useState<Map<string, BundleAdministrationTask>>(new Map());
  const [releaseId, setReleaseId] = useState("");
  const [saved, setSaved] = useState<{ releaseId: string; revision: number } | null>(null);
  const [approval, setApproval] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const saveCommand = useRef(secureKey("cmd"));
  const publishCommand = useRef(secureKey("cmd"));
  const [previewCorrelation] = useState(() => secureKey("corr"));
  const preview = useQuery(
    previewBundleRelease,
    saved === null ? "skip" : { releaseId: saved.releaseId, correlationId: previewCorrelation },
  );
  const entries = useMemo(
    () =>
      [...selected.values()].map((item) => ({
        bundleId: item.bundleId,
        bundleVersion: item.version,
        contentDigest: item.contentDigest,
        market: item.market,
      })),
    [selected],
  );
  const save = async () => {
    if (!releaseId || entries.length === 0) return;
    setFeedback(null);
    try {
      const result = await saveCandidate({
        releaseId,
        entries,
        expectedRevision: null,
        commandKey: saveCommand.current,
        correlationId,
      });
      setSaved({ releaseId: result.releaseId, revision: result.revision });
      saveCommand.current = secureKey("cmd");
      setFeedback(
        "Release candidate saved. Preview its complete market effect before publication.",
      );
    } catch {
      setFeedback(
        "The candidate was not saved. Check the exact approved versions and release reference.",
      );
    }
  };
  const publishCurrent = async () => {
    if (!saved || !preview?.ready || !confirmed) return;
    setFeedback(null);
    try {
      await publish({
        releaseId: saved.releaseId,
        expectedRevision: preview.expectedRevision,
        previewFingerprint: preview.previewFingerprint,
        approvalReference: approval,
        confirmed: true,
        commandKey: publishCommand.current,
        correlationId,
      });
      publishCommand.current = secureKey("cmd");
      setFeedback(`Published to ${environment}. Existing customer reminders were not changed.`);
      setSaved(null);
      setConfirmed(false);
    } catch {
      setFeedback("Publication was refused. Preview the current candidate and dependencies again.");
    }
  };
  if (page === undefined) return <p role="status">Reading approved bundle versions…</p>;
  return (
    <div>
      <CompletionProfileRelease environment={environment} />
      <p>
        Select exact approved versions. Ordinary publication keeps at least five active bundles in
        every launch market.
      </p>
      <button
        type="button"
        disabled={saved !== null}
        onClick={() =>
          setSelected(
            (current) =>
              new Map([
                ...current,
                ...page.items.map((item) => [`${item.bundleId}/${item.version}`, item] as const),
              ]),
          )
        }
      >
        Select this page ({page.items.length} versions)
      </button>
      <p role="status">{selected.size} versions selected across all pages.</p>
      <ul className="bundle-release-list">
        {page.items.map((item) => {
          const key = `${item.bundleId}/${item.version}`;
          return (
            <li key={key}>
              <label>
                <input
                  type="checkbox"
                  disabled={saved !== null}
                  checked={selected.has(key)}
                  onChange={(event) =>
                    setSelected((current) => {
                      const next = new Map(current);
                      if (event.target.checked) next.set(key, item);
                      else next.delete(key);
                      return next;
                    })
                  }
                />
                {item.title} · {item.market} · v{item.version}
              </label>
            </li>
          );
        })}
      </ul>
      <TaskPagination page={page} />
      <label>
        Named release reference
        <input value={releaseId} onChange={(event) => setReleaseId(event.target.value)} />
      </label>
      <button
        type="button"
        disabled={!releaseId || entries.length === 0 || saved !== null}
        onClick={() => void save()}
      >
        Save release candidate
      </button>
      {saved && preview === undefined ? (
        <p role="status">Calculating exact release effect…</p>
      ) : null}
      {preview ? (
        <ReleaseImpact preview={preview}>
          <label>
            SCRUM approval reference
            <input value={approval} onChange={(event) => setApproval(event.target.value)} />
          </label>
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            Publish this exact unchanged preview to {environment}.
          </label>
          <button
            type="button"
            disabled={!preview.ready || !confirmed || approval.trim() === ""}
            onClick={() => void publishCurrent()}
          >
            Publish reviewed bundle release
          </button>
        </ReleaseImpact>
      ) : null}
      {feedback ? <p role="status">{feedback}</p> : null}
    </div>
  );
}

export function CompletionProfileRelease({
  environment,
}: Readonly<{ environment: string }>): React.JSX.Element {
  const [correlationId] = useState(() => secureKey("corr"));
  const publishProfiles = useMutation(publishCompletionProfileCandidate);
  const publishEveryday = useMutation(publishEverydayCompletionBindings);
  const [candidate, setCandidate] = useState<"baseline" | "everyday">("baseline");
  const [busy, setBusy] = useState(false);
  const profileCommand = useRef(secureKey("cmd"));
  const [approval, setApproval] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const publish = async () => {
    if (busy || !confirmed || environment !== "shared-test") return;
    setBusy(true);
    setFeedback(null);
    try {
      const command = {
        approvalReference: approval,
        confirmed: true,
        commandKey: profileCommand.current,
        correlationId,
      };
      const result =
        candidate === "everyday"
          ? await publishEveryday({ ...command, candidateId: "scrum-427-everyday-completion-v1" })
          : await publishProfiles({
              ...command,
              candidateId: "goal-2-completion-profiles-2026-09-06-rc1",
            });
      profileCommand.current = secureKey("cmd");
      setConfirmed(false);
      setFeedback(
        `${result.publishedBindingCount} exact completion profiles published to Shared Test.`,
      );
    } catch {
      setFeedback(
        "Completion-profile publication was refused. Check the exact candidate, permissions and published presets.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="bundle-impact" aria-labelledby="completion-profile-release-heading">
      <h4 id="completion-profile-release-heading">Completion questions</h4>
      <p>
        {candidate === "everyday"
          ? "Add the three reviewed everyday-bundle type bindings while preserving the existing 81 bindings."
          : "Publish the Product Owner-reviewed four profiles across 81 reminder types."}{" "}
        This changes reference content only; existing reminders and customer history stay unchanged.
      </p>
      {environment === "shared-test" ? (
        <>
          <label htmlFor="completion-release-candidate">Completion release candidate</label>
          <select
            id="completion-release-candidate"
            value={candidate}
            disabled={busy}
            onChange={(event) => {
              const next = event.target.value;
              if (next !== "baseline" && next !== "everyday") return;
              setCandidate(next);
              setConfirmed(false);
              setApproval("");
              setFeedback(null);
              profileCommand.current = secureKey("cmd");
            }}
          >
            <option value="baseline">Original profiles — 81 types</option>
            <option value="everyday">Everyday bundles — 3 additional types</option>
          </select>
          <label>
            SCRUM approval reference
            <input
              disabled={busy}
              value={approval}
              onChange={(event) => {
                setApproval(event.target.value);
                setConfirmed(false);
                profileCommand.current = secureKey("cmd");
              }}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              disabled={busy}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            {candidate === "everyday"
              ? "Publish the exact approved 3-type addition to Shared Test."
              : "Publish the exact approved 81-type candidate to Shared Test."}
          </label>
          <button
            type="button"
            disabled={busy || !confirmed || approval.trim() === ""}
            onClick={() => void publish()}
          >
            {busy ? "Publishing completion profiles…" : "Publish reviewed completion profiles"}
          </button>
        </>
      ) : (
        <p>This candidate is not approved outside Shared Test.</p>
      )}
      {feedback ? <p role="status">{feedback}</p> : null}
    </section>
  );
}

function ReleaseImpact({
  preview,
  children,
}: Readonly<{ preview: BundleReleasePreview; children: ReactNode }>) {
  return (
    <section className="bundle-impact" aria-labelledby="bundle-release-impact-heading">
      <h4 id="bundle-release-impact-heading">Release impact</h4>
      <ul>
        {preview.resultingActiveCounts.map((entry) => (
          <li key={entry.market}>
            {entry.market}: {entry.count} active bundles
          </li>
        ))}
      </ul>
      {preview.blockers.length > 0 ? (
        <p role="alert">{preview.blockers.length} exact version dependencies need review.</p>
      ) : null}
      {children}
    </section>
  );
}

function SafetyTasks({ environment }: Readonly<{ environment: string }>): React.JSX.Element {
  const [selected, setSelected] = useState<BundleAdministrationTask | null>(null);
  const page = useTaskPage("safety");
  const [correlationId] = useState(() => secureKey("corr"));
  const preview = useQuery(
    previewBundleWithdrawal,
    selected === null
      ? "skip"
      : { bundleId: selected.bundleId, bundleVersion: selected.version, correlationId },
  );
  const withdraw = useMutation(withdrawBundleVersion);
  const [reason, setReason] = useState<
    | "content-safety"
    | "evidence-invalid"
    | "dependency-unavailable"
    | "privacy-risk"
    | "market-scope-error"
  >("content-safety");
  const [confirmed, setConfirmed] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const command = useRef(secureKey("cmd"));
  const act = async () => {
    if (!preview || !confirmed) return;
    setFeedback(null);
    try {
      const result = await withdraw({
        bundleId: preview.bundleId,
        bundleVersion: preview.bundleVersion,
        expectedRevision: preview.expectedRevision,
        contentDigest: preview.contentDigest,
        previewFingerprint: preview.previewFingerprint,
        reason,
        confirmed: true,
        commandKey: command.current,
        correlationId,
      });
      command.current = secureKey("cmd");
      setFeedback(
        result.retrospectiveReviewDueAtEpochMilliseconds === null
          ? `Version withdrawn from ${environment}. Existing reminders remain unchanged.`
          : `Version withdrawn. Independent review is due ${formatAdministrationTime(result.retrospectiveReviewDueAtEpochMilliseconds)}.`,
      );
      setSelected(null);
      setConfirmed(false);
    } catch {
      setFeedback("Withdrawal was refused. Reopen the exact current impact before trying again.");
    }
  };
  if (page === undefined) return <p role="status">Reading published bundle versions…</p>;
  return (
    <div className="bundle-task-grid">
      <TaskList
        area="safety"
        selected={selected}
        onSelect={(task) => {
          setSelected(task);
          setReason("content-safety");
          setConfirmed(false);
          setFeedback(null);
        }}
      />
      {preview ? (
        <section className="bundle-impact" aria-labelledby="bundle-withdrawal-impact-heading">
          <h4 id="bundle-withdrawal-impact-heading">Exact withdrawal impact</h4>
          <p>
            {preview.market} would have {preview.resultingPublishedCount} published bundles.
          </p>
          <p>
            {preview.activeDraftCountCapped ? "At least " : ""}
            {preview.activeDraftCount} active private drafts and{" "}
            {preview.existingCreationGroupCountCapped ? "at least " : ""}
            {preview.existingCreationGroupCount} existing creation groups retain their immutable
            version reference.
          </p>
          <label>
            Safety reason
            <select
              value={reason}
              onChange={(event) => setReason(event.target.value as typeof reason)}
            >
              <option value="content-safety">Content safety</option>
              <option value="evidence-invalid">Evidence invalid</option>
              <option value="dependency-unavailable">Dependency unavailable</option>
              <option value="privacy-risk">Privacy risk</option>
              <option value="market-scope-error">Market scope error</option>
            </select>
          </label>
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            Withdraw this exact version now. Saved reminders will not be altered.
          </label>
          <button type="button" disabled={!confirmed} onClick={() => void act()}>
            Withdraw exact bundle version
          </button>
        </section>
      ) : null}
      {feedback ? <p role="status">{feedback}</p> : null}
    </div>
  );
}
